/**
 * Corporate partners — server state (M3, Plan D15).
 *
 * A registry rather than transactional data: it changes when staff add a partner,
 * which is rare. The list is cached for a few minutes like reference data, and
 * creating one invalidates it so the picker shows the new name immediately.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../components/common/Toast';
import {
  createOrganization,
  fetchOrganizationOptions,
  fetchOrganizations,
  importOrganizationVolunteers,
} from '../services/api/organizations';
import type {
  CreateOrganizationBody,
  ImportPayload,
  OrganizationListQuery,
} from '../services/api/organizations';
import { organizationKeys, volunteerKeys } from './queryKeys';

export { organizationKeys };

const REGISTRY_STALE_TIME_MS = 5 * 60 * 1000;

export function useOrganizations(params: OrganizationListQuery = {}) {
  return useQuery({
    queryKey: organizationKeys.list(params),
    queryFn: () => fetchOrganizations(params),
    staleTime: REGISTRY_STALE_TIME_MS,
  });
}

/**
 * The partner list for the public registration form.
 *
 * Cached like the other pick-lists it sits beside: the set of partners changes
 * when staff add one, not while somebody is filling in a form.
 */
export function useOrganizationOptions() {
  return useQuery({
    queryKey: [...organizationKeys.all, 'options'] as const,
    queryFn: fetchOrganizationOptions,
    staleTime: REGISTRY_STALE_TIME_MS,
  });
}

export function useCreateOrganization() {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateOrganizationBody) => createOrganization(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: organizationKeys.all });
      toast.success('Organization added.');
    },
    onError: (error) => toast.error(error, 'Could not add the organization.'),
  });
}

/**
 * Bulk-import a corporate partner's volunteers from a CSV (item 8).
 *
 * On success the volunteer directory is invalidated, so the freshly imported
 * PENDING records appear without a manual refresh.
 */
export function useImportVolunteers(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ImportPayload) => importOrganizationVolunteers(organizationId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: volunteerKeys.all });
    },
  });
}
